import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for ACL-APD API path
 */
export const ACL_APD_API_PATH = new InjectionToken<string>('ACL_APD_API_PATH');

/**
 * Interface for access request response
 */
export interface AccessRequestResponse {
  [key: string]: any;
}

/**
 * ACL-APD API Service for handling access control and access policy domain related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class AclApdApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(ACL_APD_API_PATH) private aclApdApiPath: string,
  ) {}

  /**
   * Get access requests for organisation
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with access requests data
   */
  async getOrganisationAccessRequests(
    page: number = 1,
    size: number = 10
  ): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request/organisation?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error fetching organisation access requests:', error);
      throw error;
    }
  }

  /**
   * Get asset requests for an organisation
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Optional sort parameter (e.g., 'assetName:asc')
   * @returns Promise with asset requests data
   */
  async getAssetRequestForAnOrganisation(
    page: number = 1,
    size: number = 10,
    sort?: string
  ): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      if (sort) {
        params.append('sort', sort);
      }

      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request/organisation?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error fetching asset requests for organisation:', error);
      throw error;
    }
  }

  /**
   * Get access requests for consumer
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param requestStatus Optional request status filter (e.g., 'GRANTED', 'REJECTED', 'PENDING')
   * @returns Promise with access requests data
   */
  async getUserAccessRequests(
    page: number = 1,
    size: number = 10,
    requestStatus?: string
  ): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      // Add requestStatus filter if provided
      if (requestStatus) {
        params.append('requestStatus', requestStatus);
      }

      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request/consumer?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error fetching consumer access requests:', error);
      throw error;
    }
  }

  /**
   * Get asset requests for provider
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param requestStatus Optional request status filter (e.g., 'GRANTED', 'REJECTED', 'PENDING')
   * @param sort Optional sort (e.g. 'createdAt:desc', 'assetName:asc')
   * @returns Promise with asset requests data
   */
  async providerAssetRequests(
    page: number = 1,
    size: number = 10,
    requestStatus?: string,
    sort?: string
  ): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      // Add requestStatus filter if provided
      if (requestStatus) {
        params.append('requestStatus', requestStatus);
      }
      if (sort) {
        params.append('sort', sort);
      }

      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request/provider?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error fetching provider asset requests:', error);
      throw error;
    }
  }

  /**
   * Get asset requests for platform admin
   * Mirrors: GET /access_request/platform?page={page}&size={size}
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param requestStatus Optional request status filter
   * @returns Promise with asset requests data
   */
  async platformAssetRequests(
    page: number = 1,
    size: number = 10,
    requestStatus?: string,
    startDate?: string,
    endDate?: string,
    organizationId?: string
  ): Promise<AccessRequestResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      if (requestStatus) {
        params.append('requestStatus', requestStatus);
      }
      if (startDate) {
        params.append('time', startDate);
      }
      if (endDate) {
        params.append('endtime', endDate);
      }
      if (startDate && endDate) {
        params.append('timerel', 'between');
      }
      if (organizationId) {
        params.append('organizationId', organizationId);
      }

      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request/platform?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      return response;
    } catch (error) {
      console.error('Error fetching platform asset requests:', error);
      throw error;
    }
  }

  /**
   * Create an access request
   * @param payload Access request payload
   * @returns Promise with access request data
   */
  async createAccessRequest(payload: any): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request`;

      // Make POST API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.post_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error creating access request:', error);
      throw error;
    }
  }

  

  /**
   * Check if user has access to a resource
   * @param payload Access check payload
   * @returns Promise with access check response
   */
  async hasAccess(payload: any): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request/has_access`;

      // Make POST API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.post_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error checking access:', error);
      throw error;
    }
  }

  /**
   * Update an access request
   * @param requestId Access request ID
   * @param payload Update payload
   * @returns Promise with updated access request data
   */
  async updateAccessRequest(requestId: string, payload: any): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const params = new URLSearchParams();
      params.append('id', requestId);

      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request?${params.toString()}`;

      // Make PATCH API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.put_api(
        url,
        payload,
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error(`Error updating access request ${requestId}:`, error);
      throw error;
    }
  }

  /**
   * Withdraw an access request
   * @param itemId Item ID of the access request
   * @returns Promise with withdraw response
   */
  async withdrawRequest(itemId: string): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request/${itemId}`;

      // Make PATCH API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.patch_api(
        url,
        {}, // body - empty object
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error(`Error withdrawing access request ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * Delete an access request
   * @param requestId Access request ID
   * @returns Promise with delete response
   */
  async deleteAccessRequest(requestId: string): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const params = new URLSearchParams();
      params.append('id', requestId);

      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request?${params.toString()}`;

      // Make DELETE API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.delete_api(
        url,
        undefined, // body - not needed for DELETE with ID in query params
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error(`Error deleting access request ${requestId}:`, error);
      throw error;
    }
  }

  /**
   * Approve or reject an access request
   * @param payload Approval/rejection payload (will be sent from component)
   * @returns Promise with approval/rejection response
   */
  async approveOrReject(payload: any): Promise<AccessRequestResponse> {
    try {
      // Construct the full URL using injected basePathUrl and aclApdApiPath
      const url = `${this.basePathUrl}${this.aclApdApiPath}access_request`;

      // Make PUT API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.put_api(
        url,
        payload,
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error('Error approving/rejecting access request:', error);
      throw error;
    }
  }
}
